import { motion, useInView, useScroll, useTransform, AnimatePresence, useMotionValueEvent } from "framer-motion";
import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { GlassCard, GlassButton, GlassFeatureTile } from "@/components/ui/glass";
import { supabase } from "@/integrations/supabase/client";
import {
  Search, MapPin, CalendarDays, Users, ChevronRight, Shield, Zap, BarChart3,
  Car, Brain, Headphones, TrendingUp, Camera, Lock, Globe, Sparkles,
  CheckCircle2, Star, ArrowRight, Truck } from
"lucide-react";

import sprinterImg from "@/assets/gateway-sprinter.jpg";

import { useABTest } from "@/hooks/useABTest";
import { track } from "@/lib/analytics";

const vanHeroVideo = "/__l5e/assets-v1/fe82ef5b-a260-446c-98f5-882ab7e3ea5a/van-hero.mp4";

const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const POPULAR_CITIES = [
"Dallas, TX", "Houston, TX", "Austin, TX", "Fort Worth, TX",
"San Antonio, TX", "Miami, FL", "Los Angeles, CA", "Nashville, TN"];


/* ─── Animated counter ─── */
const AnimatedNumber = ({ value, suffix = "" }: {value: number;suffix?: string;}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setCount(Math.round((1 - Math.pow(1 - progress, 4)) * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, value]);
  return <span ref={ref}>{count}{suffix}</span>;
};

/* ─── Floating particles ─── */
const Particles = () =>
<div className="absolute inset-0 overflow-hidden pointer-events-none">
    {Array.from({ length: 15 }).map((_, i) =>
  <motion.div
    key={i}
    className="absolute w-px h-px bg-primary/30 rounded-full"
    style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
    animate={{ y: [0, -80 - Math.random() * 120], opacity: [0, 0.6, 0], scale: [0, 1.5, 0] }}
    transition={{ duration: 4 + Math.random() * 6, repeat: Infinity, delay: Math.random() * 5, ease: "easeOut" }} />

  )}
  </div>;


/* ─── Reveal ─── */
const Reveal = ({ children, className = "", delay = 0 }: {children: React.ReactNode;className?: string;delay?: number;}) =>
<motion.div
  initial={{ opacity: 0, y: 40 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.9, delay, ease: EASE }}
  viewport={{ once: true, margin: "-60px" }}
  className={className}>
  
    {children}
  </motion.div>;


/* ─── Time options ─── */
const SEARCH_TIMES = [
  "06:00 AM", "07:00 AM", "08:00 AM", "09:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM",
  "06:00 PM", "07:00 PM", "08:00 PM", "09:00 PM", "10:00 PM",
];

const TIME_VALUES: Record<string, string> = {
  "06:00 AM": "06:00", "07:00 AM": "07:00", "08:00 AM": "08:00", "09:00 AM": "09:00",
  "10:00 AM": "10:00", "11:00 AM": "11:00", "12:00 PM": "12:00", "01:00 PM": "13:00",
  "02:00 PM": "14:00", "03:00 PM": "15:00", "04:00 PM": "16:00", "05:00 PM": "17:00",
  "06:00 PM": "18:00", "07:00 PM": "19:00", "08:00 PM": "20:00", "09:00 PM": "21:00",
  "10:00 PM": "22:00",
};

const formatDateShort = (dateStr: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/* ─── Search Bar with Booking Type Toggle ─── */
const MarketplaceSearch = () => {
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [passengers, setPassengers] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(
    () => city.trim().length > 0
      ? POPULAR_CITIES.filter((c) => c.toLowerCase().includes(city.toLowerCase()))
      : POPULAR_CITIES,
    [city]
  );

  // Close date picker on outside click
  useEffect(() => {
    if (!showDatePicker) return;
    const handler = (e: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setShowDatePicker(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [showDatePicker]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (city) params.set("city", city.split(",")[0].trim());
    if (startDate) params.set("start", startDate);
    if (endDate) params.set("end", endDate);
    if (startTime) params.set("startTime", TIME_VALUES[startTime] || startTime);
    if (endTime) params.set("endTime", TIME_VALUES[endTime] || endTime);
    if (passengers) params.set("passengers", passengers);
    navigate(`/rent-a-van?${params.toString()}`);
  };

  const datesSummary = () => {
    if (!startDate && !endDate) return null;
    let text = "";
    if (startDate) text += formatDateShort(startDate);
    if (endDate) text += ` – ${formatDateShort(endDate)}`;
    if (startTime || endTime) {
      text += " • ";
      text += startTime || "Any";
      text += " – ";
      text += endTime || "Any";
    }
    return text;
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <GlassCard intensity="strong" hover={false} className="p-3 md:p-4 max-w-5xl mx-auto !bg-transparent !border-0 !shadow-none" style={{ background: "radial-gradient(ellipse 100% 100% at 50% 50%, hsl(0 0% 6% / 0.8) 40%, hsl(0 0% 6% / 0.4) 70%, transparent 100%)" }}>
      {/* ─── DESKTOP: Single horizontal row ─── */}
      <div className="hidden md:flex items-stretch gap-0">
        {/* Location */}
        <div className="relative flex-1 group">
          <div className="flex items-center gap-3 px-4 py-3 rounded-l-xl hover:bg-foreground/[0.03] transition-colors">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[9px] tracking-[0.25em] uppercase text-foreground/60 font-medium mb-0.5">Location</p>
              <input
                type="text"
                value={city}
                onChange={(e) => {setCity(e.target.value);setShowSuggestions(true);}}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="City or area"
                className="bg-transparent text-sm text-foreground placeholder:text-foreground/50 w-full outline-none font-medium" />
            </div>
          </div>
          {showSuggestions && filtered.length > 0 &&
          <div className="absolute top-full left-0 right-0 mt-1 bg-card/95 backdrop-blur-xl border border-border/30 rounded-xl py-2 z-50 shadow-xl">
              {!city.trim() && <p className="px-5 py-1.5 text-[9px] tracking-[0.2em] uppercase text-foreground/40">Popular locations</p>}
              {filtered.map((c) =>
            <button
              key={c}
              onMouseDown={() => {setCity(c);setShowSuggestions(false);}}
              className="w-full text-left px-5 py-2.5 text-sm text-foreground hover:bg-foreground/[0.05] flex items-center gap-3 transition-colors">
                  <MapPin className="w-3.5 h-3.5 text-primary/60" /> {c}
                </button>
            )}
            </div>
          }
        </div>

        <div className="w-px bg-foreground/[0.08] my-2" />

        {/* Dates + Times — unified field */}
        <div className="relative" ref={datePickerRef}>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-foreground/[0.03] transition-colors text-left min-w-[260px]"
          >
            <CalendarDays className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[9px] tracking-[0.25em] uppercase text-foreground/60 font-medium mb-0.5">Trip Dates & Times</p>
              <p className={`text-sm font-medium ${datesSummary() ? "text-foreground" : "text-foreground/50"}`}>
                {datesSummary() || "Select dates"}
              </p>
            </div>
          </button>

          {/* Date+Time picker dropdown */}
          <AnimatePresence>
            {showDatePicker && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 mt-2 bg-card/95 backdrop-blur-xl border border-border/30 rounded-xl p-5 z-50 shadow-2xl min-w-[340px]"
              >
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-foreground/60 font-medium block mb-1.5">Pickup Date</label>
                    <input
                      type="date"
                      value={startDate}
                      min={today}
                      onChange={(e) => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(e.target.value); }}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none focus:border-primary/40 transition-colors [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-foreground/60 font-medium block mb-1.5">Return Date</label>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate || today}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none focus:border-primary/40 transition-colors [color-scheme:dark]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-foreground/60 font-medium block mb-1.5">Pickup Time</label>
                    <select
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none appearance-none cursor-pointer focus:border-primary/40 transition-colors"
                    >
                      <option value="" className="bg-card">Any time</option>
                      {SEARCH_TIMES.map((t) => <option key={t} value={t} className="bg-card">{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-foreground/60 font-medium block mb-1.5">Return Time</label>
                    <select
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none appearance-none cursor-pointer focus:border-primary/40 transition-colors"
                    >
                      <option value="" className="bg-card">Any time</option>
                      {SEARCH_TIMES.map((t) => <option key={t} value={t} className="bg-card">{t}</option>)}
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="w-full py-2 text-xs tracking-[0.15em] uppercase text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Done
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="w-px bg-foreground/[0.08] my-2" />

        {/* Passengers */}
        <div className="flex items-center gap-3 px-4 py-3 hover:bg-foreground/[0.03] transition-colors">
          <Users className="w-4 h-4 text-primary flex-shrink-0" />
          <div className="min-w-[100px]">
            <p className="text-[9px] tracking-[0.25em] uppercase text-foreground/60 font-medium mb-0.5">Passengers</p>
            <select
              value={passengers}
              onChange={(e) => setPassengers(e.target.value)}
              className="bg-transparent text-sm text-foreground w-full outline-none appearance-none cursor-pointer font-medium">
              <option value="" className="bg-card">Any</option>
              {[6, 8, 10, 12, 14].map((n) =>
              <option key={n} value={String(n)} className="bg-card">Up to {n}</option>
              )}
            </select>
          </div>
        </div>

        {/* Search button */}
        <button
          onClick={handleSearch}
          className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-r-xl px-8 py-3 flex items-center justify-center gap-2.5 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 flex-shrink-0">
          <Search className="w-4 h-4" />
          <span className="text-xs tracking-[0.2em] uppercase font-semibold">Search</span>
        </button>
      </div>

      {/* ─── MOBILE: Compact stacked layout ─── */}
      <div className="flex flex-col gap-2 md:hidden">
        {/* Location */}
        <div className="relative">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-foreground/[0.03]">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[9px] tracking-[0.25em] uppercase text-foreground/60 font-medium mb-0.5">Location</p>
              <input
                type="text"
                value={city}
                onChange={(e) => {setCity(e.target.value);setShowSuggestions(true);}}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="City or area"
                className="bg-transparent text-sm text-foreground placeholder:text-foreground/50 w-full outline-none font-medium" />
            </div>
          </div>
          {showSuggestions && filtered.length > 0 &&
          <div className="absolute top-full left-0 right-0 mt-1 bg-card/95 backdrop-blur-xl border border-border/30 rounded-xl py-2 z-50 shadow-xl">
              {!city.trim() && <p className="px-5 py-1.5 text-[9px] tracking-[0.2em] uppercase text-foreground/40">Popular locations</p>}
              {filtered.map((c) =>
            <button
              key={c}
              onMouseDown={() => {setCity(c);setShowSuggestions(false);}}
              className="w-full text-left px-5 py-2.5 text-sm text-foreground hover:bg-foreground/[0.05] flex items-center gap-3 transition-colors">
                  <MapPin className="w-3.5 h-3.5 text-primary/60" /> {c}
                </button>
            )}
            </div>
          }
        </div>

        {/* Dates + Times combined field */}
        <div className="relative" ref={showDatePicker ? undefined : datePickerRef}>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-foreground/[0.03] text-left"
          >
            <CalendarDays className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[9px] tracking-[0.25em] uppercase text-foreground/60 font-medium mb-0.5">Trip Dates & Times</p>
              <p className={`text-sm font-medium ${datesSummary() ? "text-foreground" : "text-foreground/50"}`}>
                {datesSummary() || "Select dates & times"}
              </p>
            </div>
          </button>

          {/* Mobile date picker panel */}
          <AnimatePresence>
            {showDatePicker && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 right-0 mt-2 bg-card/95 backdrop-blur-xl border border-border/30 rounded-xl p-4 z-50 shadow-2xl"
              >
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-foreground/60 font-medium block mb-1">Pickup</label>
                    <input
                      type="date"
                      value={startDate}
                      min={today}
                      onChange={(e) => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(e.target.value); }}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none focus:border-primary/40 [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-foreground/60 font-medium block mb-1">Return</label>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate || today}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none focus:border-primary/40 [color-scheme:dark]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-foreground/60 font-medium block mb-1">Pickup Time</label>
                    <select
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none appearance-none cursor-pointer focus:border-primary/40"
                    >
                      <option value="" className="bg-card">Any</option>
                      {SEARCH_TIMES.map((t) => <option key={t} value={t} className="bg-card">{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-foreground/60 font-medium block mb-1">Return Time</label>
                    <select
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="bg-foreground/[0.04] border border-foreground/10 rounded-lg px-3 py-2.5 text-sm text-foreground w-full outline-none appearance-none cursor-pointer focus:border-primary/40"
                    >
                      <option value="" className="bg-card">Any</option>
                      {SEARCH_TIMES.map((t) => <option key={t} value={t} className="bg-card">{t}</option>)}
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="w-full py-2 text-xs tracking-[0.15em] uppercase text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Done
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Passengers row */}
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-foreground/[0.03]">
          <Users className="w-4 h-4 text-primary flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[9px] tracking-[0.25em] uppercase text-foreground/60 font-medium mb-0.5">Passengers</p>
            <select
              value={passengers}
              onChange={(e) => setPassengers(e.target.value)}
              className="bg-transparent text-sm text-foreground w-full outline-none appearance-none cursor-pointer font-medium">
              <option value="" className="bg-card">Any</option>
              {[6, 8, 10, 12, 14].map((n) =>
              <option key={n} value={String(n)} className="bg-card">Up to {n}</option>
              )}
            </select>
          </div>
        </div>

        {/* Search button — prominent, full width */}
        <button
          onClick={handleSearch}
          className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl px-6 py-4 flex items-center justify-center gap-2.5 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 mt-1">
          <Search className="w-4 h-4" />
          <span className="text-sm tracking-[0.2em] uppercase font-semibold">Search Vehicles</span>
        </button>
      </div>
    </GlassCard>);

};




/* ─── Ecosystem Card ─── */
const EcosystemCard = ({ icon: Icon, title, description, cta, to, index

}: {icon: any;title: string;description: string;cta: string;to: string;index: number;}) =>
<Reveal delay={index * 0.1}>
    <Link to={to} onClick={() => window.scrollTo(0, 0)}>
      <GlassCard intensity="medium" className="p-6 md:p-8 group cursor-pointer h-full flex flex-col">
        <div className="w-12 h-12 rounded-xl bg-primary/[0.08] border border-primary/[0.12] flex items-center justify-center mb-5 group-hover:bg-primary/[0.15] group-hover:scale-110 transition-all duration-500">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <h3 className="font-serif text-xl md:text-2xl text-foreground mb-3 leading-[1.1]">{title}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed flex-1 mb-6">{description}</p>
        <div className="flex items-center gap-3">
          <GlassButton variant="secondary" size="sm" className="group-hover:border-primary/40 group-hover:text-primary pointer-events-none">
            {cta} <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </GlassButton>
        </div>
      </GlassCard>
    </Link>
  </Reveal>;


/* ─── Dual Marketplace Gateway Card ─── */
const GatewayCard = ({ image, icon: Icon, title, description, cta, to, delay

}: {image: string;icon: any;title: string;description: string;cta: string;to: string;delay: number;}) =>
<Reveal delay={delay}>
    <Link to={to} onClick={() => window.scrollTo(0, 0)}>
      <GlassCard intensity="medium" className="overflow-hidden group cursor-pointer h-full">
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
          src={image}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 backdrop-blur-md border border-primary/20 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-foreground">{title}</h3>
            </div>
          </div>
        </div>
        <div className="p-6 md:p-8">
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">{description}</p>
          <GlassButton variant="secondary" size="sm" className="group-hover:border-primary/40 group-hover:text-primary pointer-events-none">
            {cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </GlassButton>
        </div>
      </GlassCard>
    </Link>
  </Reveal>;


/* ─── Marquee ─── */
const Marquee = () => {
  const items = ["Rent", "Host", "Commission", "Acquire", "Manage", "Deliver"];
  return (
    <div className="overflow-hidden py-5 border-y border-[#B8960C]/10 relative">
      <div className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{ background: "linear-gradient(to right, #0A0A0A, transparent)" }} />
      <div className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{ background: "linear-gradient(to left, #0A0A0A, transparent)" }} />
      <motion.div className="flex gap-16 whitespace-nowrap" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 24, repeat: Infinity, ease: "linear" }}>
        {[...items, ...items, ...items, ...items].map((item, i) => (
          <span key={i} className="flex items-center gap-16">
            <span className="text-[11px] tracking-[0.35em] uppercase text-[#F5ECD7]/20 font-light">{item}</span>
            <span className="w-1 h-1 rounded-full bg-[#B8960C]/40" />
          </span>
        ))}
      </motion.div>
    </div>
  );
};

/* ─── Gold Particles ─── */
const GoldParticles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i, x: Math.random() * 100, y: Math.random() * 100,
    size: 0.5 + Math.random() * 1.5, duration: 6 + Math.random() * 10,
    delay: Math.random() * 8, drift: (Math.random() - 0.5) * 60,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <motion.div key={p.id} className="absolute rounded-full"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, background: `rgba(184,150,12,0.4)`, boxShadow: `0 0 ${p.size * 3}px rgba(184,150,12,0.4)` }}
          animate={{ y: [-20, -140], x: [0, p.drift], opacity: [0, 0.8, 0], scale: [0, 1.2, 0] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: "easeOut" }} />
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   GATEWAY PAGE — Dual Marketplace Platform Hub
═══════════════════════════════════════════════════ */
// ─── Headline copy variants ────────────────────────────────────────────────────
const HEADLINE_VARIANTS: Record<string, { line1: string; line2: string }> = {
  control:   { line1: "Rent. Host.",  line2: "Own."                 },
  "variant-a": { line1: "Luxury Vans,", line2: "Your Way."          },
  "variant-b": { line1: "The Van",     line2: "Is Waiting."         },
};

// ─── Subheading copy variants ──────────────────────────────────────────────────
const SUBHEADING_VARIANTS: Record<string, string> = {
  control:     "Find premium luxury vans from verified hosts.",
  "variant-a": "Browse verified luxury vans. Book instantly. Go anywhere.",
  "variant-b": "Premium vans for weddings, events, and everyday luxury.",
};

const GatewayPage = () => {
  const [showHostModal, setShowHostModal] = useState(false);
  const navigate = useNavigate();
  const heroRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // ─── A/B Experiments ────────────────────────────────────────────────────────
  const headlineVariant = useABTest("gateway-hero-headline");
  const subheadingVariant = useABTest("gateway-hero-subheading");

  const headline = HEADLINE_VARIANTS[headlineVariant] ?? HEADLINE_VARIANTS.control;
  const subheading = SUBHEADING_VARIANTS[subheadingVariant] ?? SUBHEADING_VARIANTS.control;

  useEffect(() => {
    track.gateway.experimentViewed("gateway-hero-headline", headlineVariant);
    track.gateway.experimentViewed("gateway-hero-subheading", subheadingVariant);
  }, [headlineVariant, subheadingVariant]);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroScroll, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(heroScroll, [0, 0.6], [1, 0]);
  const videoOpacity = useTransform(heroScroll, [0, 0.7], [0.32, 0]);

  // Autoplay video — simple and smooth
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {});
  }, []);





  return (
    <>
      <SEOHead
        title="Vauren | Premium Luxury Van Rentals, Hosting & Buying"
        description="The premium luxury van platform. Rent a luxury van, list your van to earn, or commission a custom build."
        canonical="/" />
      

      <div className="min-h-screen bg-background grain-overlay">

        {/* ═══════ SECTION 1: HERO ═══════ */}
        <section ref={heroRef} className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
          {/* Obsidian base + radial depth */}
          <div className="absolute inset-0 bg-[#0A0A0A]" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 70% at 50% 50%, #1a1a1a 0%, #0A0A0A 100%)" }} />

          {/* Hero video — autoplay with scroll-driven parallax + fade */}
          <motion.video
            ref={videoRef}
            src={vanHeroVideo}
            muted
            playsInline
            autoPlay
            loop
            preload="auto"
            style={{
              opacity: videoOpacity,
              mixBlendMode: "luminosity",
              y: heroY,
            }}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />

          {/* Grain texture */}
          <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.03 }}>
            <svg width="100%" height="100%">
              <filter id="hero-grain"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
              <rect width="100%" height="100%" filter="url(#hero-grain)" />
            </svg>
          </div>

          {/* Gold top line */}
          <div className="absolute top-0 left-0 right-0 h-[1px] z-10" style={{ background: "linear-gradient(90deg, transparent, #B8960C 30%, #B8960C 70%, transparent)", opacity: 0.4 }} />

          {/* Atmospheric orb — bottom left */}
          <div className="absolute pointer-events-none" style={{ bottom: "10%", left: "15%", width: "400px", height: "400px", borderRadius: "50%", background: "radial-gradient(circle, rgba(184,150,12,0.03) 0%, transparent 70%)", filter: "blur(40px)" }} />

          {/* Gold particles */}
          <GoldParticles />

          {/* Ghosted parallax wordmark */}
          <motion.div className="absolute inset-0 flex items-start justify-center pointer-events-none select-none pt-[8vh]" style={{ y: heroY }}>
            <span className="font-serif font-light text-[#F5ECD7]" style={{ fontSize: "clamp(100px, 18vw, 260px)", letterSpacing: "0.15em", opacity: 0.04, whiteSpace: "nowrap" }}>VAUREN</span>
          </motion.div>

          {/* Sign in — top right */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }} className="absolute top-6 right-6 md:top-8 md:right-10 z-30">
            <Link to="/marketplace/auth" className="text-[11px] tracking-[0.12em] uppercase text-[#F5ECD7]/40 hover:text-[#F5ECD7] transition-colors duration-200">
              Sign in
            </Link>
          </motion.div>

          {/* Center content */}
          <motion.div style={{ opacity: heroOpacity }} className="relative z-20 text-center px-6 w-full max-w-4xl">
            {/* Eyebrow */}
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="text-[#B8960C] text-[10px] uppercase tracking-[0.35em] mb-6">
              The Luxury Van Marketplace
            </motion.p>

            {/* Gold line */}
            <motion.div initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }} transition={{ delay: 0.3, duration: 1.2, ease: EASE }} className="w-[120px] h-px bg-[#B8960C] mx-auto mb-10 origin-center" />

            {/* Headline — A/B tested */}
            <div className="overflow-hidden mb-4">
              <motion.h1 initial={{ y: 60 }} animate={{ y: 0 }} transition={{ delay: 0.5, duration: 1.2, ease: EASE }} className="font-serif font-light text-[#F5ECD7] leading-[1]" style={{ fontSize: "clamp(48px, 7vw, 96px)" }}>
                {headline.line1} <span className="italic">{headline.line2}</span>
              </motion.h1>
            </div>

            {/* Subheading — A/B tested */}
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }} className="text-[#F5ECD7]/60 text-[15px] max-w-xl mx-auto leading-relaxed mb-10">
              {subheading}
            </motion.p>

            {/* Search bar */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease: EASE }}>
              <MarketplaceSearch />
            </motion.div>

          </motion.div>

          {/* Scroll indicator */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2">
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#F5ECD7]/30">Scroll</p>
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="w-px h-8" style={{ background: "linear-gradient(to bottom, rgba(184,150,12,0.6), transparent)" }} />
          </motion.div>
        </section>

        {/* ═══════ MARQUEE ═══════ */}
        <Marquee />

        {/* ═══════ QUICK-ACCESS NAVIGATION TILES ═══════ */}
        <section className="py-12 px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { label: "Rent", sub: "Self-drive luxury vans", href: "/rent-a-van", icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="15" height="13" rx="2" /><path d="M16 8h4l3 5v4h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
              )},
              { label: "Host", sub: "List your van & earn", href: "/host", icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              )},
              { label: "Manage", sub: "Fleet tools & operations", href: "/manage-a-fleet", icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                </svg>
              )},
              { label: "Buy", sub: "Commission a build", href: "/buy-a-van", icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
              )},
            ].map((tile, i) => (
              <motion.div
                key={tile.label}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: EASE }}
                whileHover={{ y: -6, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to={tile.href}
                  onClick={() => window.scrollTo(0, 0)}
                  className="group relative flex flex-col items-center gap-4 p-7 md:p-8 rounded-xl overflow-hidden transition-all duration-500"
                  style={{
                    background: "linear-gradient(135deg, rgba(245,236,215,0.04) 0%, rgba(184,150,12,0.03) 50%, rgba(245,236,215,0.02) 100%)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    border: "1px solid rgba(245,236,215,0.06)",
                    boxShadow: "0 4px 30px rgba(0,0,0,0.3), inset 0 1px 0 rgba(245,236,215,0.06)",
                  }}
                >
                  {/* Hover glow overlay */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none rounded-xl"
                    style={{
                      background: "radial-gradient(ellipse at center, rgba(184,150,12,0.08) 0%, transparent 70%)",
                    }}
                  />
                  {/* Top highlight line */}
                  <div
                    className="absolute top-0 left-[10%] right-[10%] h-px opacity-40 group-hover:opacity-80 transition-opacity duration-500"
                    style={{ background: "linear-gradient(90deg, transparent, rgba(184,150,12,0.5), transparent)" }}
                  />
                  {/* Bottom accent line on hover */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-center"
                    style={{ background: "linear-gradient(90deg, transparent, #B8960C, transparent)" }}
                  />

                  {/* Icon */}
                  <div className="text-[#F5ECD7]/50 group-hover:text-[#B8960C] transition-colors duration-500 relative z-10">
                    {tile.icon}
                  </div>
                  {/* Label */}
                  <span className="text-[14px] tracking-[0.12em] uppercase text-[#F5ECD7]/90 group-hover:text-[#F5ECD7] transition-colors duration-300 font-medium relative z-10">
                    {tile.label}
                  </span>
                  {/* Subtitle */}
                  <span className="text-[10px] tracking-[0.05em] text-[#F5ECD7]/30 group-hover:text-[#F5ECD7]/50 transition-colors duration-300 -mt-2 relative z-10">
                    {tile.sub}
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ═══════ PLATFORM STATS ═══════ */}
        <section className="py-24 md:py-32 px-6 relative overflow-hidden">
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(184,150,12,0.025) 0%, transparent 70%)" }} />
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(to right, transparent, rgba(184,150,12,0.15), transparent)" }} />
          <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "linear-gradient(to right, transparent, rgba(184,150,12,0.15), transparent)" }} />
          <div className="max-w-5xl mx-auto relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
              {[
                { value: 2500, suffix: "+", label: "Avg Monthly Revenue" },
                { value: 85, suffix: "%", label: "Utilization Rate" },
                { value: 4, suffix: ".9★", label: "Average Host Rating" },
                { value: 24, suffix: "/7", label: "Platform Support" },
              ].map((stat, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="text-center group">
                    <p className="font-serif text-4xl md:text-5xl text-[#F5ECD7] leading-none mb-3 group-hover:text-primary transition-colors duration-500">
                      <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                    </p>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#F5ECD7]/35 mt-4">{stat.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ SECTION 5: PLATFORM ECOSYSTEM ═══════ */}
        <section className="py-16 md:py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/[0.015] rounded-full blur-[150px] pointer-events-none" />

          <div className="max-w-5xl mx-auto relative z-10">
            <Reveal className="text-center mb-10 md:mb-16">
              <span className="text-[9px] tracking-[0.5em] uppercase text-primary/70 block mb-5">The Platform</span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.1] tracking-[0.02em]">
                Three Pillars,<br /><span className="italic">One Platform</span>
              </h2>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              <EcosystemCard
                index={0}
                icon={Truck}
                title="Rent a Luxury Van"
                description="Browse premium luxury vans for weddings, corporate travel, airport transfers, and luxury group transportation."
                cta="Explore Vans"
                to="/rent-a-van" />
              
              <EcosystemCard
                index={1}
                icon={TrendingUp}
                title="Host Your Van"
                description="List your van on the marketplace and generate passive revenue from bookings and events."
                cta="Become a Host"
                to="/host" />
              
              <EcosystemCard
                index={2}
                icon={BarChart3}
                title="Manage a Fleet"
                description="Fleet operators can manage vehicles, track performance, handle owners, and scale operations with advanced tools."
                cta="Fleet Management"
                to="/manage-a-fleet" />
              
            </div>
          </div>
        </section>

        {/* ═══════ VAN TIERS ═══════ */}
        <section className="py-24 md:py-36 px-6 md:px-12 lg:px-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0D0D0B] to-transparent" />
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(to right, transparent, rgba(184,150,12,0.12), transparent)" }} />
          <div className="max-w-7xl mx-auto relative z-10">
            <Reveal className="text-center mb-16 md:mb-24">
              <span className="text-[9px] tracking-[0.5em] uppercase text-primary/60 block mb-5">Travel Classes</span>
              <div className="w-12 h-px bg-[#B8960C]/30 mx-auto mb-8" />
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.05]">
                Four levels of<br /><span className="italic">private travel</span>
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  tag: "Vauren I",
                  title: "The Standard",
                  subtitle: "Premium configuration for discerning everyday travel.",
                  includes: ["Up to 8 passengers", "Premium audio system", "Climate-controlled cabin", "Tinted privacy glass", "USB + wireless charging"],
                  to: "/rent-a-van",
                  highlight: false,
                },
                {
                  tag: "Vauren II",
                  title: "The Executive",
                  subtitle: "Extended wheelbase. Elevated finish. Zero compromise.",
                  includes: ["Up to 14 passengers", "Captain's chair seating", "Privacy partition", "Entertainment display", "Ambient lighting"],
                  to: "/rent-a-van",
                  highlight: true,
                },
                {
                  tag: "Vauren III",
                  title: "The Commission",
                  subtitle: "Bespoke builds for extended engagements.",
                  includes: ["Fully bespoke interior", "Dedicated chauffeur", "Mobile office setup", "Starlink connectivity", "White-glove on-demand service"],
                  to: "/rent-a-van",
                  highlight: false,
                },
                {
                  tag: "Vauren IV",
                  title: "The Atelier",
                  subtitle: "By introduction only. Beyond specification.",
                  includes: ["One-of-one build", "Coachbuilder access", "Full rear suite", "Onboard concierge", "Legacy documentation"],
                  to: "/rent-a-van",
                  highlight: false,
                },
              ].map((tier, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <Link to={tier.to} onClick={() => window.scrollTo(0, 0)}>
                    <div className={`relative h-full border rounded-sm p-8 flex flex-col group transition-all duration-500 overflow-hidden ${tier.highlight ? "border-[#B8960C]/30 bg-[#B8960C]/[0.03]" : "border-[#F5ECD7]/[0.06] hover:border-[#B8960C]/20"}`}>
                      <div className={`absolute top-0 left-0 right-0 h-px transition-all duration-500 ${tier.highlight ? "bg-[#B8960C]/50" : "bg-[#B8960C]/0 group-hover:bg-[#B8960C]/30"}`} />
                      <span className="text-[10px] tracking-[0.3em] uppercase text-[#B8960C] mb-6 block">{tier.tag}</span>
                      <h3 className="font-serif text-2xl text-[#F5ECD7] leading-[1.05] mb-3">{tier.title}</h3>
                      <p className="text-sm text-[#F5ECD7]/40 leading-relaxed mb-8 font-light flex-1">{tier.subtitle}</p>
                      <div className="border-t border-[#F5ECD7]/[0.06] pt-6 mb-6 space-y-3">
                        {tier.includes.map((item, ii) => (
                          <div key={ii} className="flex items-center gap-3">
                            <div className="w-1 h-1 rounded-full bg-[#B8960C]/60 flex-shrink-0" />
                            <span className="text-xs text-[#F5ECD7]/50 font-light">{item}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-[#F5ECD7]/40 group-hover:text-[#B8960C] transition-colors duration-300">
                        Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ SECTION 6: HOSTING OPPORTUNITY ═══════ */}
        <section className="py-24 md:py-48 px-6 md:px-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-card/20 to-background" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/20 to-transparent" />

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <Reveal>
                <span className="text-[9px] tracking-[0.5em] uppercase text-primary/70 block mb-8">Earn With Your Vehicle</span>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.1] mb-6 tracking-[0.02em]">
                  Turn Your Vehicle Into<br />
                  <span className="italic text-primary/80">a Revenue-Generating Asset</span>
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed font-light mb-10 max-w-lg">
                  Owners can list their vans on the marketplace and earn income through bookings, events, and transportation demand.
                </p>

                <div className="space-y-4 mb-10">
                  {[
                  "AI-powered pricing optimization for maximum earnings",
                  "Automated booking management and calendar sync",
                  "Professional marketing tools to promote your vehicles",
                  "Secure payouts and transparent financial reporting"].
                  map((item, i) =>
                  <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-foreground/80 font-light">{item}</p>
                    </div>
                  )}
                </div>

                <Link to="/host" onClick={() => window.scrollTo(0, 0)}>
                  <GlassButton variant="primary">
                    Start Hosting <ArrowRight className="w-3.5 h-3.5" />
                  </GlassButton>
                </Link>
              </Reveal>

              <Reveal delay={0.2}>
                <GlassCard intensity="strong" hover={false} className="p-8 md:p-10">
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="text-center">
                      <p className="font-serif text-3xl md:text-4xl text-primary"><AnimatedNumber value={2500} suffix="+" /></p>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/50 mt-2">Avg Monthly Revenue</p>
                    </div>
                    <div className="text-center">
                      <p className="font-serif text-3xl md:text-4xl text-primary"><AnimatedNumber value={85} suffix="%" /></p>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/50 mt-2">Utilization Rate</p>
                    </div>
                  </div>
                  <div className="w-full h-px bg-foreground/[0.06] mb-8" />
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <p className="font-serif text-3xl md:text-4xl text-primary"><AnimatedNumber value={4} suffix=".9" /></p>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/50 mt-2">Host Rating</p>
                    </div>
                    <div className="text-center">
                      <p className="font-serif text-3xl md:text-4xl text-primary"><AnimatedNumber value={24} suffix="/7" /></p>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/50 mt-2">Platform Support</p>
                    </div>
                  </div>
                </GlassCard>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ═══════ SECTION 7: FLEET MANAGEMENT ═══════ */}
        <section className="py-24 md:py-36 px-6 md:px-12 lg:px-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <Reveal delay={0.1}>
                <GlassCard intensity="medium" hover={false} className="p-8 md:p-10">
                  <div className="space-y-6">
                    {[
                    { icon: BarChart3, label: "Revenue Tracking", desc: "Real-time fleet earnings and performance analytics" },
                    { icon: Car, label: "Vehicle Management", desc: "Manage your entire van fleet in one dashboard" },
                    { icon: Users, label: "Owner Relationships", desc: "Assign and manage vehicle owners seamlessly" },
                    { icon: TrendingUp, label: "Growth Tools", desc: "Scale operations with AI-powered fleet intelligence" }].
                    map(({ icon: FIcon, label, desc }, i) =>
                    <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary/[0.08] border border-primary/[0.1] flex items-center justify-center flex-shrink-0">
                          <FIcon className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-serif text-base text-foreground mb-1">{label}</p>
                          <p className="text-xs text-muted-foreground/60 font-light">{desc}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </GlassCard>
              </Reveal>

              <Reveal>
                <span className="text-[9px] tracking-[0.5em] uppercase text-primary/70 block mb-8">Fleet Operations</span>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.1] mb-6 tracking-[0.02em]">
                  Manage Your Fleet<br />
                  <span className="italic text-primary/80">With Precision</span>
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed font-light mb-10 max-w-lg">
                  Fleet operators can manage multiple vans, owners, bookings, availability, and operations through one powerful dashboard.
                </p>
                <Link to="/manage-a-fleet" onClick={() => window.scrollTo(0, 0)}>
                  <GlassButton variant="primary">
                    Manage a Fleet <ArrowRight className="w-3.5 h-3.5" />
                  </GlassButton>
                </Link>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ═══════ SECTION 8: PLATFORM TECHNOLOGY ═══════ */}
        <section className="py-24 md:py-36 px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <Reveal className="text-center mb-14 md:mb-20">
              <span className="text-[9px] tracking-[0.5em] uppercase text-primary/70 block mb-5">Platform Technology</span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.1] tracking-[0.02em]">
                Powered by <span className="italic">Intelligence</span>
              </h2>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <GlassFeatureTile
                icon={<Brain className="w-5 h-5 text-primary" />}
                title="AI Listing Optimization"
                description="Smart algorithms optimize your listing title, description, photos, and pricing to maximize booking conversions across both marketplaces." />
              
              <GlassFeatureTile
                icon={<Headphones className="w-5 h-5 text-primary" />}
                title="Host Success Manager"
                description="AI-powered success advisor provides personalized recommendations and proactive insights to grow your hosting business." />
              
              <GlassFeatureTile
                icon={<BarChart3 className="w-5 h-5 text-primary" />}
                title="Fleet Management Dashboard"
                description="Complete fleet oversight with revenue tracking, vehicle utilization, driver coordination, and owner management tools." />
              
              <GlassFeatureTile
                icon={<TrendingUp className="w-5 h-5 text-primary" />}
                title="Demand Intelligence"
                description="Real-time market data and pricing analytics help you position vehicles for maximum occupancy and revenue." />
              
              <GlassFeatureTile
                icon={<Sparkles className="w-5 h-5 text-primary" />}
                title="Marketing & Growth Tools"
                description="Professional marketing resources, QR codes, social media kits, and AI content generation to promote your vehicles." />
              
              <GlassFeatureTile
                icon={<Globe className="w-5 h-5 text-primary" />}
                title="Multi-City Marketplace"
                description="List and manage vehicles across multiple cities with dedicated landing pages that drive local traffic and bookings." />
              
            </div>
          </div>
        </section>

        {/* ═══════ SECTION 9: TRUST LAYER ═══════ */}
        <section className="py-20 md:py-28 px-6 md:px-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-card/15 to-background" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/20 to-transparent" />

          <div className="max-w-5xl mx-auto relative z-10">
            <Reveal className="text-center mb-14">
              <h2 className="font-serif text-3xl md:text-4xl text-foreground leading-[1.1] tracking-[0.02em]">
                Built on <span className="italic">Trust</span>
              </h2>
            </Reveal>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
              { icon: Lock, label: "Secure Payouts", desc: "Stripe-powered processing" },
              { icon: BarChart3, label: "Transparent Reporting", desc: "Real-time financial data" },
              { icon: Shield, label: "Verified Vehicles", desc: "Quality-checked listings" },
              { icon: Star, label: "Rated & Reviewed", desc: "Community-driven trust" }].
              map(({ icon: Icon, label, desc }, i) =>
              <Reveal key={label} delay={i * 0.08}>
                  <GlassCard intensity="light" hover={false} className="p-6 text-center">
                    <div className="w-10 h-10 rounded-xl bg-primary/[0.08] border border-primary/[0.1] flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <p className="font-serif text-base text-foreground mb-1">{label}</p>
                    <p className="text-[11px] text-muted-foreground/60 font-light">{desc}</p>
                  </GlassCard>
                </Reveal>
              )}
            </div>
          </div>
        </section>

        {/* ═══════ SECTION 10: FINAL CONVERSION ═══════ */}
        <section className="py-24 md:py-36 px-6 md:px-12">
          <Reveal className="max-w-4xl mx-auto text-center">
            <div className="arch-divider mb-14" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.1] mb-6 tracking-[0.02em]">
              Ready to <span className="italic">Get Started</span>?
            </h2>
            <p className="text-muted-foreground text-sm max-w-xl mx-auto leading-relaxed font-light mb-12">
              Whether you're renting premium transportation, listing a luxury vehicle,
              or managing an elite fleet — your journey starts here.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
              <Link to="/rent-a-van" onClick={() => window.scrollTo(0, 0)}>
                <GlassButton variant="primary" size="lg">
                  <Truck className="w-4 h-4" /> Rent
                </GlassButton>
              </Link>
              <Link to="/host" onClick={() => window.scrollTo(0, 0)}>
                <GlassButton variant="secondary" size="lg">
                  Host
                </GlassButton>
              </Link>
              <Link to="/manage-a-fleet" onClick={() => window.scrollTo(0, 0)}>
                <GlassButton variant="secondary" size="lg">
                  Manage
                </GlassButton>
              </Link>
              <Link to="/buy-a-van" onClick={() => window.scrollTo(0, 0)}>
                <GlassButton variant="secondary" size="lg">
                  Buy
                </GlassButton>
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 mt-14">
              <Link to="/how-it-works" onClick={() => window.scrollTo(0, 0)} className="group flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-muted-foreground/50 hover:text-primary transition-colors duration-500">
                How It Works
                <span className="block w-6 h-px bg-current group-hover:w-10 transition-all duration-500" />
              </Link>
              <Link to="/rent-a-van" onClick={() => window.scrollTo(0, 0)} className="group flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-muted-foreground/50 hover:text-primary transition-colors duration-500">
                Browse Marketplace
                <span className="block w-6 h-px bg-current group-hover:w-10 transition-all duration-500" />
              </Link>
              <Link to="/about" onClick={() => window.scrollTo(0, 0)} className="group flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-muted-foreground/50 hover:text-primary transition-colors duration-500">
                About Us
                <span className="block w-6 h-px bg-current group-hover:w-10 transition-all duration-500" />
              </Link>
            </div>
          </Reveal>
        </section>

        {/* ═══════ FOOTER ═══════ */}
        <footer className="border-t border-border/10 py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-16">
              <div className="md:col-span-2">
                <p className="font-serif text-lg text-foreground mb-3">Vauren</p>
                <p className="text-sm text-muted-foreground/50 font-light leading-relaxed max-w-sm">
                  The premium luxury van platform. Rent, host, manage, or commission your next build.
                </p>
              </div>
              <div>
                <p className="text-[9px] tracking-[0.3em] uppercase text-muted-foreground/40 mb-4">Marketplace</p>
                <div className="space-y-3">
                  <Link to="/rent-a-van" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-muted-foreground/50 hover:text-primary transition-colors duration-500">Rent a Van</Link>
                  <Link to="/host" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-muted-foreground/50 hover:text-primary transition-colors duration-500">Host a Van</Link>
                  <Link to="/manage-a-fleet" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-muted-foreground/50 hover:text-primary transition-colors duration-500">Fleet Management</Link>
                  <Link to="/how-it-works" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-muted-foreground/50 hover:text-primary transition-colors duration-500">How It Works</Link>
                </div>
              </div>
              <div>
                <p className="text-[9px] tracking-[0.3em] uppercase text-muted-foreground/40 mb-4">Company</p>
                <div className="space-y-3">
                  <Link to="/about" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-muted-foreground/50 hover:text-primary transition-colors duration-500">About Us</Link>
                  <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-muted-foreground/50 hover:text-primary transition-colors duration-500">Contact</Link>
                  <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-muted-foreground/50 hover:text-primary transition-colors duration-500">Blog</Link>
                </div>
              </div>
            </div>
            <div className="border-t border-border/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <span className="text-[9px] tracking-[0.3em] uppercase text-muted-foreground/50">
                © {new Date().getFullYear()} Vauren
              </span>
              <div className="flex items-center gap-6">
                <Link to="/service-agreement" onClick={() => window.scrollTo(0, 0)} className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/50 hover:text-muted-foreground/70 transition-colors">Terms</Link>
                <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/50 hover:text-muted-foreground/70 transition-colors">Contact</Link>
              </div>
            </div>
          </div>
        </footer>
        {/* Host Vehicle Type Modal */}
        <AnimatePresence>
          {showHostModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center px-6"
              onClick={() => setShowHostModal(false)}>
              <div className="absolute inset-0 bg-background/80 backdrop-blur-md" />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 w-full max-w-lg glass-strong p-8 md:p-10 rounded-2xl border border-foreground/[0.08]">
                <button
                  onClick={() => setShowHostModal(false)}
                  className="absolute top-4 right-4 text-muted-foreground/50 hover:text-foreground transition-colors">
                  <X className="w-5 h-5" />
                </button>
                <p className="text-[10px] tracking-[0.4em] uppercase text-primary mb-3">Host on Vauren</p>
                <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-2 leading-[1.1]">
                  Ready to host your van?
                </h3>
                <p className="text-muted-foreground text-sm mb-8">List your van on Vauren and start earning. We handle bookings, payments, and support.</p>
                <button
                  onClick={() => { setShowHostModal(false); navigate("/host"); window.scrollTo(0, 0); }}
                  className="w-full py-4 bg-primary hover:bg-primary/90 text-primary-foreground text-[11px] uppercase tracking-[0.25em] font-semibold transition-colors duration-200">
                  Get started
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>);

};

export default GatewayPage;